import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { IconButton } from "@mui/material";
import SnDndFormControls from "@shared/components/SnDndFormControls";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DrawingButtons from "./DesignerButtons";
import { globalMenu, onSearchStore } from "@shared/libs/Global";
import { ChatSearchFormData } from "@shared/type/CommonType";

interface DrawingSearchPanelProps {
  data: any;
  controls: any[];
  popupRef?: any;
  isPopup?: boolean;
  onValueChanged?: (event: any) => void;
}

type InputEls = {
  el: HTMLElement | null;
  isFocusable: boolean;
};

const DrawingSearchPanel = forwardRef(
  (
    {
      data,
      controls,
      popupRef,
      isPopup,
      onValueChanged,
    }: DrawingSearchPanelProps,
    ref
  ) => {
    const [formData, setFormData] = useState<any>();
    const [visibleControls, setVisibleControls] = useState<any[]>([]);
    const [controlsData, setControlsData] = useState<any[]>([]);
    const [editCon, setEditCon] = useState<any>();
    const [filter, setFilter] = useState<any>();
    const [isExpand, setIsExpand] = useState(false);
    const [expandcss, setExpandcss] = useState(
      "overflow-hidden transition-all duration-500 ease-in-out max-h-[500px] w-full"
    );

    const panelRef = useRef<HTMLDivElement>(null); // Panel 영역을 참조
    const inputEls = useRef<InputEls[]>([]);
    const [popupCons, setPopupCons] = useState<any>({}); /* */
    const formDataRef = useRef(formData);

    const formValueChanged = useCallback((nextData: ChatSearchFormData) => {
      formDataRef.current = nextData;
      setFormData(nextData);
    }, []);

    const getAllowedFieldNames = useCallback(() => {
      const fieldNames = new Set<string>();
      (visibleControls || []).forEach((control) => {
        String(control?.cd_param || "")
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
          .forEach((fieldName) => fieldNames.add(fieldName));
        const displayFieldName = String(control?.cd_displaycolumn || "").trim();
        if (displayFieldName) {
          fieldNames.add(displayFieldName);
        }
      });
      return Array.from(fieldNames);
    }, [visibleControls]);

    const fetch = async () => {
      try {
        formValueChanged(data || {});
        const visible = controls?.filter(
          (control) => control.fg_visible === "Y"
        );
        setVisibleControls(visible || []);
        setControlsData(controls || []);
      } catch (error: any) {}
    };

    useEffect(() => {
      fetch();
    }, [controls, data, formValueChanged]);

    useEffect(() => {
      const menuCode =
        (controls || []).find((control) => control?.cd_menu)?.cd_menu ||
        globalMenu.getState().activeMenuInfo?.cd_menu;
      if (!menuCode) return;

      onSearchStore.getState().registerSearchPanel(menuCode, {
        getFormdata: () => formDataRef.current,
        setFormdata: (nextData) => formValueChanged(nextData || {}),
        getAllowedFieldNames,
      });
      return () => {
        onSearchStore.getState().unregisterSearchPanel(menuCode);
      };
    }, [controls, formValueChanged, getAllowedFieldNames]);

    const gridContainerStyle = {
      display: "grid",
      // gridTemplateColumns: "repeat(13, minmax(85px, 1fr))",
      gridTemplateColumns: isPopup
        ? "repeat(6, minmax(125px, 1fr))"
        : "repeat(13, minmax(85px, 1fr))",
      gridTemplateRows: "repeat(auto-fill, 1fr)",
      overflowX: "auto" as "auto",
      gap: "10px",
      padding: "11px",
    };

    const moveControl = (fromIdx: number, toIdx: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1); // 1. 기존 위치에서 제거
        updated.splice(toIdx, 0, moved); // 2. 목표 인덱스에 삽입
        return updated;
      });
    };

    const removeControl = (index: number, id: number) => {
      setVisibleControls((prev) => {
        const updated = [...prev];
        updated.splice(index, 1); // 1. 기존 위치에서 제거
        return updated;
      });
    };

    const resizeControl = (
      id: number,
      colspan: number,
      rowspan: number,
      x: number,
      y: number
    ) => {
      const newColSpan = Math.round(x / 85) + colspan;
      const newRowSpan = Math.round(y / 32) + rowspan;

      setVisibleControls((prev) =>
        prev.map((c) =>
          c.no_line === id
            ? { ...c, qt_colspan: newColSpan, qt_rowspan: newRowSpan }
            : c
        )
      );
    };

    const handleExpand = (flag?: any) => {
      const f = typeof flag === "boolean" ? !flag : !isExpand;
      if (f) {
        setExpandcss(
          "overflow-hidden transition-all duration-500 ease-in-out max-h-[42px] w-full"
        );
      } else {
        setExpandcss(
          "overflow-hidden transition-all duration-500 ease-in-out max-h-[500px] w-full"
        );
      }
      setIsExpand(f);
    };

    useImperativeHandle(ref, () => ({
      getControls: () => {
        return visibleControls;
      },
      getFormdata: () => {
        return formDataRef.current;
      },
      getFormData: () => {
        return formDataRef.current;
      },
      setFormdata: (nextData: ChatSearchFormData) => {
        formValueChanged(nextData || {});
      },
      getAllowedFieldNames,
      applyPopupConditions: (popupConditions: any) => {
        setPopupCons(popupConditions);
      },
    }));

    const setInputRef = (
      el: HTMLElement | null,
      index: number,
      isReadOnly: boolean
    ) => {
      inputEls.current[index] = {
        el,
        isFocusable: !isReadOnly,
      };
    };

    const handleMoveFocus = (fromIndex: number, target: string) => {
      if (target === "current") {
        const el = inputEls.current?.[fromIndex].el;
        if (el) {
          el.focus();
        }
      } else if (target === "next") {
        const list = inputEls.current;
        for (let i = fromIndex + 1; i < list.length; i++) {
          const entry = list[i];
          if (entry?.el && entry.isFocusable) {
            entry.el.focus();
            return;
          }
        }

        // 끝까지 없으면 현재 blur
        list[fromIndex]?.el?.blur?.();
      }
    };

    const setReadOnly = useCallback(
      (param: string) => {
        if (!editCon || !Object.prototype.hasOwnProperty.call(editCon, param)) {
          return false;
        }

        const condition = editCon[param];

        // 문자열 조건을 안전하게 평가 (true/false로 예상되는 JS 식)
        try {
          return Boolean(
            Function(`"use strict"; return (${condition})`)() &&
              String(formDataRef.current?.[param] ?? "").length > 0
          );
        } catch (e) {
          console.warn(
            `Invalid expression for editCon[${param}]:`,
            condition,
            e
          );
          return false;
        }
      },
      [editCon]
    );

    return (
      <div className="w-full">
        <div className={expandcss}>
          <div style={gridContainerStyle} ref={panelRef}>
            <DndProvider backend={HTML5Backend}>
              {visibleControls != null &&
                visibleControls.length > 0 &&
                visibleControls.map((control, index) => {
                  return (
                    <SnDndFormControls
                      labletype="SearchPanel"
                      control={control}
                      id={control.no_line}
                      index={index}
                      formData={formData}
                      requireds={{}}
                      popupCons={popupCons}
                      setRef={() => {}}
                      setInputRef={setInputRef}
                      handleMoveFocus={handleMoveFocus}
                      onTextValueChanged={() => {}}
                      onNumberValueChanged={() => {}}
                      onPeriodValueChanged={() => {}}
                      onCodeValueChanged={() => {}}
                      setReadOnly={setReadOnly}
                      onButtonClick={() => {}}
                      isSearchPanel={true}
                      popupRef={popupRef}
                      moveControl={moveControl}
                      removeControl={removeControl}
                      resizeControl={resizeControl}
                      filter={filter}
                    />
                  );
                })}
            </DndProvider>
          </div>
        </div>
        <div className="flex justify-center h-[15px]">
          <IconButton
            onClick={handleExpand}
            sx={{
              color: "gray", // 기본 색상
              "&:hover": {
                color: "blue", // hover 시 색상
                transform: "scale(1.2)", // hover 시 크기 확대
                transition: "all 0.3s ease", // 부드러운 애니메이션
              },
            }}
          >
            {!isExpand && <ArrowDropUpIcon />}
            {isExpand && <ArrowDropDownIcon />}
          </IconButton>
        </div>
        <hr
          style={{ height: "1px", background: "rgb(186,191,199)", border: 0 }}
        />
        <div className="relative">
          <DrawingButtons
            type="조회조건"
            data={controlsData}
            onAdd={(value: any[]) => {
              const updatedControls = [...visibleControls, ...value[0]].map(
                (col, idx) => ({
                  ...col,
                  no_seq: idx + 1,
                  no_line: idx + 1,
                  cd_form: "search",
                })
              );

              // 2. 새 폼 데이터 계산
              const updatedFormData = { ...formData };
              value[0].forEach((item: any) => {
                updatedFormData[item.cd_param] = item.cd_value;
              });

              // 3. 상태 업데이트
              setVisibleControls(updatedControls);
              setFormData(updatedFormData);

              if (onValueChanged) {
                onValueChanged({
                  controls: updatedControls,
                  data: updatedFormData,
                });
              }
            }}
            onChange={(value: any[]) => {
              // 1. 업데이트할 데이터를 미리 계산합니다.
              const updatedFormData = { ...formData }; // 현재 formData 상태 복사
              value.forEach((item: any) => {
                updatedFormData[item.cd_param] = item.cd_value;
              });

              // 2. 계산된 데이터로 상태를 업데이트합니다.
              setVisibleControls(value);
              setFormData(updatedFormData);

              if (onValueChanged) {
                onValueChanged({
                  controls: value,
                  data: updatedFormData,
                });
              }
            }}
            onSetting={(value: any) => {}}
          />
        </div>
      </div>
    );
  }
);

export default DrawingSearchPanel;
